import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AUTOPILOT_HEARTBEAT_MINUTES, autopilotHeartbeatView } from '@brunel/shared';
import type { Guard } from '../App';
import type { AutopilotHeartbeatRead } from './RoadmapAutopilot';
import { setAutopilotHeartbeatOnce } from '../lib/mutations';

function clockTime(iso: string): string {
  const at = new Date(iso);
  return Number.isFinite(at.getTime()) ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Unknown';
}

/** Settings: pause or resume the 15-minute Overseer heartbeat. The switch persists. */
export function AutopilotHeartbeatSettings({ client, guard, orgId, read, nowMs, onChanged }: {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly read: AutopilotHeartbeatRead | null;
  readonly nowMs: number;
  readonly onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ readonly text: string; readonly alert: boolean } | null>(null);
  const view = autopilotHeartbeatView({
    heartbeat: read?.heartbeat ?? null, latestRound: read?.latestRound ?? null, nowMs, time: clockTime,
  });
  const paused = read?.heartbeat?.paused ?? false;
  const readable = read !== null;

  const toggle = () => {
    const next = !paused;
    setMessage(null);
    // set_autopilot_heartbeat is a direct verified-owner mutation; reuse the existing owner
    // step-up gate, as the Planner Host setting does.
    void guard('post_owner_command', async () => {
      setBusy(true);
      try {
        const saved = await setAutopilotHeartbeatOnce(client, { orgId, paused: next });
        if (saved.outcome === 'unknown') {
          setMessage({ text: 'Brunel could not confirm the change; the current setting is reread below.', alert: true });
        } else if (saved.outcome === 'rejected') {
          setMessage({ text: `Not saved (${saved.code}).`, alert: true });
        } else if (saved.paused) {
          setMessage({
            text: saved.roundRunning
              ? 'Paused. The round already running takes no further action; no new round starts.'
              : 'Paused. No new round starts until you resume.',
            alert: false,
          });
        } else {
          setMessage({ text: 'Resumed. The next round keeps its scheduled time, or runs within a minute if that time has passed.', alert: false });
        }
      } finally {
        setBusy(false);
        await onChanged();
      }
    });
  };

  return (
    <section className="stack" aria-label="Overseer heartbeat">
      <p className="muted">
        Every {AUTOPILOT_HEARTBEAT_MINUTES} minutes the Overseer runs one Autopilot round on its own: it evaluates approved
        work and new planning inputs, stops idle Workers, starts approved work on eligible Workers within capacity, and
        checks build progress. Main Command gets one short note only when something changed. No browser or chat
        message is needed.
      </p>
      <p data-heartbeat-state={view.state}>
        <strong>{paused ? 'Paused' : readable ? 'Running on schedule' : 'Unknown'}</strong>
        {view.last !== null && <> · {view.last} · {view.next}</>}
      </p>
      <div className="row">
        <button type="button" disabled={busy || !readable} onClick={toggle} aria-pressed={paused}>
          {paused ? 'Resume heartbeat' : 'Pause heartbeat'}
        </button>
      </div>
      <small className="muted">
        Pausing stops new rounds. A round already running stops before its next action; anything it already did stays done.
      </small>
      {message !== null && (message.alert ? <p role="alert">{message.text}</p> : <p role="status">{message.text}</p>)}
    </section>
  );
}
